import { faFileLines } from "@fortawesome/free-regular-svg-icons";
import { faCheck, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useParams } from "react-router-dom";
import { GET_LEAVE_REQUEST, UPDATE_LEAVE_REQUEST_STATUS } from "../../../../config/config";
import { api, getToken } from "../../../../services/apis/auth-api/apiUrl";
import { useAuth } from "../../../../services/auth-services/authContext";
import { AuthContextState } from "../../../../utils/types";
import AccessDenied from "../../error/accessDenied";
import PageExpired from "../../error/pageExpired";

interface LeaveRequestProps {
    req_id : number;
    leave_type: string;
    days_on_leave: number;
    leave_dates : string;
    leave_body: string;
    status: string;
    date_requested: string; 
    first_name: string;
    last_name: string;
}

interface leaveRequestByIdProps {
    id : number;
    days_on_leave : number; 
    leave_type : string;
    date_requested : string;
    status : string;
}

interface leaveCred {
    total_vl : number;
    total_sl : number;
    earned_vl : number;
    used_vl : number;
    used_sl : number;
    leave_data : leaveRequestByIdProps[];
}


const LeaveApproval = () => { 
    const [approverId, setApproverId] = useState<number>(0);
    const { token } = useParams();
    const { decodedJwtData } = useAuth() as AuthContextState;
    const account_id = decodedJwtData?.userId;
    const [leaveReq, setLeaveReq] = useState<LeaveRequestProps | null>(null);
    const [leaveCred, setLeaveCred] = useState<leaveCred | null>(null);
    const [reqStat, setReqStat] = useState<string>('');
    const [buttonLoading, setButtonLoading] = useState<boolean>(true);
    const [dateLeaves, setDateLeaves] = useState<string[]>([]);

    const formatDate = (dateString: string | undefined): string => {
        if(!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const handleUpdateRequest = async(stat : string) => {
        try{
            if(!leaveReq?.req_id) return false;
            setButtonLoading(true);
            await api.put(UPDATE_LEAVE_REQUEST_STATUS+leaveReq.req_id, {
                stat : stat
            }, {
                headers: { 'Authorization': 'Bearer ' + getToken() }
            });
            setReqStat(stat);
            setButtonLoading(false);
        }catch(e){
            alert('Something went wrong!')
            setButtonLoading(false);
        }
    }

    useEffect(()=>{
        const getLeaveRequest = async() => {
            try{
                 const api_data = await api.get(GET_LEAVE_REQUEST+token, {
                    headers: { 'Authorization': 'Bearer ' + getToken() }
                });
                const approver_id = api_data.data.approver_id;
                setApproverId(approver_id);

                const leave_data = api_data.data.leave_req_data as LeaveRequestProps;
                setLeaveReq(leave_data);
                setReqStat(leave_data.status);
                setDateLeaves(JSON.parse(leave_data.leave_dates));
                
                const leave_cred = api_data.data.leave_credits as leaveCred;
                setLeaveCred(leave_cred);

                setButtonLoading(false);
            }catch(e){
                // const error = e as AxiosError;
                // if(error.response?.status === 401){
                //     setIsAuthenticated(false);
                //     handeShowSigninModal();
                // }
            }
        }
        getLeaveRequest();
    },[account_id, token]);
    
    if(!leaveReq || !leaveCred) return <PageExpired />;
    if(approverId !== account_id) return <AccessDenied />;

    console.log(leaveReq.days_on_leave)
    const remaining_vl = (leaveCred.total_vl+ leaveCred.earned_vl) - leaveCred.used_vl;
    const remaining_sl = leaveCred.total_sl - leaveCred.used_sl;
    return (
        <div className="content flex">
             <Helmet>
                <title>Leave Approval | {leaveReq.last_name}, {leaveReq.first_name}</title>
            </Helmet>
            <div className='main-content px-4 flex justify-center overflow-y-auto flex-grow'>
                <div className="text-gray-700 min-h-full shadow-md">
                    <div className="relative min-w-full sm:min-w-[42rem] xl:min-w-[59rem] xl:max-w-[59rem] bg-white min-h-full p-4">
                        <div className={buttonLoading ? 'modal-loader' : 'hidden'}>
                            <FontAwesomeIcon className="animate-spin" icon={faSpinner} />
                        </div>
                        <div className="my-4">
                            <h1 className="font-medium text:xs md:text-xl">
                                <FontAwesomeIcon icon={faFileLines} />&nbsp;
                                Leave Approval Request
                            </h1>
                        </div>
                        <hr className="border-gray-500 border-1 shadow-md" />
                        <div className="my-2">
                            <div className="my-2">
                                <h2 className="font-medium uppercase text-xs md:text-md">{leaveReq.first_name} {leaveReq.last_name}'s Leave credits:</h2>
                            </div>
                            <div className="flex flex-wrap justify-center gap-4 my-4">
                                <div className="p-2 w-full text-xs md:text-md md:w-auto shadow-md bg-gray-100">
                                    <div className="text-center uppercase text-orange-500">
                                        Annual Leave Credits
                                    </div>
                                    <div className="flex justify-center items-center w-full">
                                        <div className="w-full text-center border-r-orange-500 border-r">
                                            <h1 className="underline">VL</h1>
                                            <p className="text-xl font-medium text-gray-500">{leaveCred?.total_vl}</p>
                                        </div>
                                        <div className="w-full text-center">
                                            <h1 className="underline">SL</h1>
                                            <p className="text-xl font-medium text-gray-500">{leaveCred?.total_sl}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-2 w-full md:w-auto shadow-md bg-gray-100">
                                    <div className="text-center uppercase text-orange-500">
                                        Converted Credits
                                    </div>
                                    <div className="flex justify-center items-center w-full">
                                        <div className="w-full text-center">
                                            <h1 className="underline">Converted SL to VL</h1>
                                            <p className="text-xl font-medium text-green-500">+{leaveCred?.earned_vl} VL</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-2 w-full md:w-auto shadow-md bg-gray-100">
                                    <div className="text-center uppercase text-orange-500">
                                        Total Used Leave 
                                    </div>
                                    <div className="flex justify-center items-center w-full">
                                        <div className="w-full text-center border-r-orange-500 border-r">
                                            <h1 className="underline">VL</h1>
                                            <p className="text-xl font-medium text-gray-500">{leaveCred.used_vl}</p>
                                        </div>
                                        <div className="w-full text-center">
                                            <h1 className="underline">SL</h1>
                                            <p className="text-xl font-medium text-gray-500">{leaveCred.used_sl}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="p-2 w-full md:w-auto shadow-md bg-gray-100">
                                    <div className="text-center uppercase text-orange-500">
                                        Remaining Leave Credits
                                    </div>
                                    <div className="flex justify-center items-center w-full">
                                        <div className="w-full text-center border-r-orange-500 border-r">
                                            <h1 className="underline">VL</h1>
                                            <p className="text-xl font-medium text-green-500">{remaining_vl >= 0 ? remaining_vl : 0}</p>
                                        </div>
                                        <div className="w-full text-center">
                                            <h1 className="underline">SL</h1>
                                            <p className="text-xl font-medium text-green-500">{remaining_sl >= 0 ? remaining_sl : 0}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr className="border-gray-500 border-1 shadow-md" />
                        <div className="my-4 flex flex-col gap-2 ml-6 text-xs md:text-md">
                            <div className="flex gap-2 item-center">
                                <p>Subject:</p>
                                <h2 className="font-medium text-md">{leaveReq.leave_type === 'vl' ? 'Vacation' : 'Sick/Emergency'} leave request</h2>
                            </div>
                            <div className="flex gap-2 item-center">
                                <p>Requested by:</p>
                                <h2 className="font-medium text-md">{leaveReq.first_name} {leaveReq.last_name}</h2>
                            </div>
                            <div className="flex gap-2 item-center">
                                <p>Date requested:</p>
                                <h2 className="font-medium text-md">{formatDate(leaveReq.date_requested)}</h2>
                            </div>
                            <div className="flex gap-2 item-center">
                                <p>Date of leave:</p>
                                <h2 className="font-medium text-md">
                                    {dateLeaves.map(date => (
                                        formatDate(date)+', '
                                    ))}
                                </h2>
                            </div>
                            <div className="flex gap-2 item-center">
                                <p>No. of leave/s:</p>
                                <h2 className="font-medium text-md">{leaveReq.days_on_leave}</h2>
                            </div>
                            <div className="flex gap-2 item-center">
                                <p>Status:</p>
                                <h2 className={reqStat === 'Approved' ? "font-medium text-md bg-green-500 text-white px-2 rounded" : reqStat === 'Pending' ? "font-medium text-md bg-yellow-500 text-white px-2 rounded" : "font-medium text-md bg-red-400 text-white px-2 rounded"}>{reqStat}</h2>
                            </div>
                        </div>
                        <hr className="border-gray-500 border-1 shadow-md" />
                        <div>
                            <div className="px-6 my-4">
                                <p>
                                {leaveReq.leave_body}
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col md:flex-row gap-2 md:absolute w-full end-1 justify-center md:w-96 md:bottom-4 md:end-4">
                            {(reqStat === 'Pending' || reqStat === 'Something went wrong!') &&(
                                <>
                                <button type="button" onClick={()=>handleUpdateRequest('Declined')} className="button button-red text-sm">
                                    <FontAwesomeIcon icon={faTimes} />&nbsp;
                                    Decline
                                </button>
                                <button type="button" onClick={()=>handleUpdateRequest('Approved')} className="button button-vcc-green text-sm">
                                    <FontAwesomeIcon icon={faCheck} />&nbsp;
                                    Approve
                                </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default LeaveApproval;